import * as THREE from 'three';
import { BlockId } from '../world/BlockType.js';
import { ItemId } from '../items/ItemRegistry.js';

// ============================================================
// BossSystem.js - Boss encounters (Wither + Ender Dragon)
// ============================================================

const PX = 1 / 16;

function simpleBox(w, h, d, color) {
  return new THREE.Mesh(
    new THREE.BoxGeometry(w, h, d),
    new THREE.MeshLambertMaterial({ color })
  );
}

function emissiveBox(w, h, d, color) {
  return new THREE.Mesh(
    new THREE.BoxGeometry(w, h, d),
    new THREE.MeshBasicMaterial({ color })
  );
}

// ============================================================
// WITHER MESH BUILDER
// ============================================================

function buildWitherMesh() {
  const group = new THREE.Group();
  const S = PX * 1.8 * 3; // 3x scale of normal mobs

  const bodyColor = 0x333333;
  const headColor = 0x222222;
  const ribColor  = 0x2A2A2A;
  const eyeColor  = 0xFFFFFF;

  // === Central body (wide, dark) ===
  const body = simpleBox(12 * S, 8 * S, 6 * S, bodyColor);
  body.position.set(0, 0, 0);
  group.add(body);

  // === Shoulder ridge ===
  const shoulder = simpleBox(14 * S, 2 * S, 5 * S, ribColor);
  shoulder.position.set(0, 5 * S, 0);
  group.add(shoulder);

  // === Rib cage (thin dark boxes below body) ===
  for (let i = 0; i < 4; i++) {
    const rib = simpleBox(10 * S - i * 1.5 * S, 1.5 * S, 4 * S, ribColor);
    rib.position.set(0, -4 * S - i * 2.5 * S, 0);
    group.add(rib);
  }

  // === Tail / spine (narrowing downward) ===
  for (let i = 0; i < 3; i++) {
    const spine = simpleBox(3 * S - i * 0.8 * S, 2 * S, 3 * S - i * 0.8 * S, bodyColor);
    spine.position.set(0, -14 * S - i * 2.5 * S, 0);
    group.add(spine);
  }

  // === Three heads ===
  const heads = [];
  const headPositions = [
    { x: 0, y: 8 * S },       // Center head (slightly higher)
    { x: -7 * S, y: 6 * S },  // Left head
    { x: 7 * S, y: 6 * S },   // Right head
  ];

  for (let hi = 0; hi < 3; hi++) {
    const headGroup = new THREE.Group();

    // Skull
    const skull = simpleBox(5 * S, 5 * S, 5 * S, headColor);
    headGroup.add(skull);

    // Forehead ridge
    const ridge = simpleBox(5.5 * S, 1.5 * S, 5.5 * S, 0x1A1A1A);
    ridge.position.set(0, 3 * S, 0);
    headGroup.add(ridge);

    // Eyes (white, glowing)
    const eyeL = emissiveBox(1.2 * S, 1.2 * S, 0.5 * S, eyeColor);
    eyeL.position.set(-1.3 * S, 1 * S, 2.8 * S);
    headGroup.add(eyeL);
    const eyeR = emissiveBox(1.2 * S, 1.2 * S, 0.5 * S, eyeColor);
    eyeR.position.set(1.3 * S, 1 * S, 2.8 * S);
    headGroup.add(eyeR);

    // Mouth / jaw
    const jaw = simpleBox(4 * S, 1.5 * S, 5.2 * S, 0x1A1A1A);
    jaw.position.set(0, -2.5 * S, 0);
    headGroup.add(jaw);

    // Teeth
    for (let t = -1; t <= 1; t++) {
      const tooth = simpleBox(0.6 * S, 0.8 * S, 0.4 * S, 0xDDDDDD);
      tooth.position.set(t * 1.2 * S, -1.5 * S, 2.8 * S);
      headGroup.add(tooth);
    }

    // Neck connecting head to body
    const neck = simpleBox(2 * S, 3 * S, 2 * S, bodyColor);
    neck.position.set(0, -4 * S, 0);
    headGroup.add(neck);

    headGroup.position.set(headPositions[hi].x, headPositions[hi].y, 0);
    group.add(headGroup);
    heads.push(headGroup);
  }

  // === Dark aura particles (small emissive cubes) ===
  for (let i = 0; i < 8; i++) {
    const particle = emissiveBox(0.4 * S, 0.4 * S, 0.4 * S, 0x4444AA);
    particle.position.set(
      (Math.random() - 0.5) * 16 * S,
      (Math.random() - 0.5) * 20 * S,
      (Math.random() - 0.5) * 8 * S
    );
    particle.userData.isAuraParticle = true;
    group.add(particle);
  }

  return { group, heads };
}

// ============================================================
// SKULL PROJECTILE
// ============================================================

function createSkullProjectile() {
  const group = new THREE.Group();

  // Dark skull shape (small box cluster)
  const skull = simpleBox(0.5, 0.5, 0.5, 0x222222);
  group.add(skull);

  // Glowing eyes
  const eyeL = emissiveBox(0.15, 0.12, 0.1, 0xFFFFFF);
  eyeL.position.set(-0.12, 0.05, 0.28);
  group.add(eyeL);
  const eyeR = emissiveBox(0.15, 0.12, 0.1, 0xFFFFFF);
  eyeR.position.set(0.12, 0.05, 0.28);
  group.add(eyeR);

  // Dark trail effect
  const trail = emissiveBox(0.2, 0.2, 0.6, 0x333355);
  trail.position.set(0, 0, -0.4);
  group.add(trail);

  return group;
}

// ============================================================
// ENDER DRAGON MESH BUILDER
// ============================================================

function buildDragonMesh() {
  const group = new THREE.Group();
  const S = PX * 1.8 * 3; // Same scale as Wither

  const bodyColor = 0x1A1A2E;
  const wingColor = 0x1A1A2E;
  const eyeColor  = 0x8E44AD;
  const jawColor  = 0x111122;

  // === Main body (elongated) ===
  const body = simpleBox(6 * S, 5 * S, 14 * S, bodyColor);
  body.position.set(0, 0, 0);
  group.add(body);

  // === Head (angular, pointed) ===
  const headGroup = new THREE.Group();

  const skull = simpleBox(4 * S, 3.5 * S, 5 * S, bodyColor);
  headGroup.add(skull);

  // Snout (narrower front)
  const snout = simpleBox(3 * S, 2.5 * S, 3 * S, jawColor);
  snout.position.set(0, -0.5 * S, 3.5 * S);
  headGroup.add(snout);

  // Horns
  const hornL = simpleBox(0.8 * S, 2.5 * S, 0.8 * S, jawColor);
  hornL.position.set(-1.5 * S, 2.5 * S, -1 * S);
  hornL.rotation.x = -0.3;
  headGroup.add(hornL);
  const hornR = simpleBox(0.8 * S, 2.5 * S, 0.8 * S, jawColor);
  hornR.position.set(1.5 * S, 2.5 * S, -1 * S);
  hornR.rotation.x = -0.3;
  headGroup.add(hornR);

  // Eyes (purple, glowing)
  const eyeL = emissiveBox(1.0 * S, 1.0 * S, 0.5 * S, eyeColor);
  eyeL.position.set(-1.2 * S, 0.5 * S, 2.8 * S);
  headGroup.add(eyeL);
  const eyeR = emissiveBox(1.0 * S, 1.0 * S, 0.5 * S, eyeColor);
  eyeR.position.set(1.2 * S, 0.5 * S, 2.8 * S);
  headGroup.add(eyeR);

  // Neck
  const neck = simpleBox(3 * S, 3 * S, 4 * S, bodyColor);
  neck.position.set(0, -1 * S, -4 * S);
  headGroup.add(neck);

  headGroup.position.set(0, 2 * S, 10 * S);
  group.add(headGroup);

  // === Wings (flat planes, ~8 block wingspan each side) ===
  const wingL = new THREE.Group();
  const wingPlaneL = simpleBox(16 * S, 0.5 * S, 10 * S, wingColor);
  wingPlaneL.position.set(-8 * S, 0, 0);
  wingL.add(wingPlaneL);
  // Wing bone
  const wingBoneL = simpleBox(16 * S, 1.0 * S, 1.5 * S, jawColor);
  wingBoneL.position.set(-8 * S, 0.3 * S, 4 * S);
  wingL.add(wingBoneL);
  wingL.position.set(0, 2 * S, 0);
  group.add(wingL);

  const wingR = new THREE.Group();
  const wingPlaneR = simpleBox(16 * S, 0.5 * S, 10 * S, wingColor);
  wingPlaneR.position.set(8 * S, 0, 0);
  wingR.add(wingPlaneR);
  const wingBoneR = simpleBox(16 * S, 1.0 * S, 1.5 * S, jawColor);
  wingBoneR.position.set(8 * S, 0.3 * S, 4 * S);
  wingR.add(wingBoneR);
  wingR.position.set(0, 2 * S, 0);
  group.add(wingR);

  // === Tail segments (narrowing) ===
  const tailSegments = [];
  for (let i = 0; i < 4; i++) {
    const seg = simpleBox(
      (4 - i) * S, (3 - i * 0.5) * S, 3 * S, bodyColor
    );
    seg.position.set(0, -i * 0.5 * S, -8 * S - i * 3 * S);
    group.add(seg);
    tailSegments.push(seg);
  }
  // Tail spike
  const spike = simpleBox(1 * S, 1 * S, 2 * S, jawColor);
  spike.position.set(0, -2 * S, -20 * S);
  group.add(spike);

  // === Legs (stubby, tucked under body) ===
  for (const side of [-1, 1]) {
    const leg = simpleBox(1.5 * S, 4 * S, 2 * S, bodyColor);
    leg.position.set(side * 3 * S, -4.5 * S, -2 * S);
    group.add(leg);
    const claw = simpleBox(2 * S, 1 * S, 2.5 * S, jawColor);
    claw.position.set(side * 3 * S, -7 * S, -2 * S);
    group.add(claw);
  }

  // === Dark aura particles ===
  for (let i = 0; i < 10; i++) {
    const particle = emissiveBox(0.3 * S, 0.3 * S, 0.3 * S, 0x6622AA);
    particle.position.set(
      (Math.random() - 0.5) * 20 * S,
      (Math.random() - 0.5) * 10 * S,
      (Math.random() - 0.5) * 20 * S
    );
    particle.userData.isAuraParticle = true;
    group.add(particle);
  }

  return { group, headGroup, wingL, wingR, tailSegments };
}

// ============================================================
// END CRYSTAL MESH
// ============================================================

function buildCrystalMesh() {
  const group = new THREE.Group();

  // Rotating cube (emissive pink)
  const crystal = emissiveBox(1, 1, 1, 0xE74C8B);
  crystal.rotation.set(Math.PI / 4, Math.PI / 4, 0);
  group.add(crystal);

  // Small emissive glow surround
  const glow = emissiveBox(1.3, 1.3, 1.3, 0xE74C8B);
  glow.material.transparent = true;
  glow.material.opacity = 0.25;
  glow.rotation.set(Math.PI / 4, Math.PI / 4, 0);
  group.add(glow);

  return group;
}

// ============================================================
// BOSS HEALTH BAR UI
// ============================================================

function createBossHealthBar(bossName = 'WITHER', barGradient = 'linear-gradient(to bottom, #AA44DD, #7722AA)', lowGradient = 'linear-gradient(to bottom, #DD4444, #AA2222)', hpColor = '#CCAAEE', borderColor = 'rgba(40, 0, 60, 0.8)') {
  const container = document.createElement('div');
  container.id = 'boss-health-bar';
  container.style.cssText = `
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    width: 60%;
    max-width: 600px;
    z-index: 900;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.5s ease;
    font-family: 'Courier New', monospace;
  `;

  // Boss name
  const nameEl = document.createElement('div');
  nameEl.style.cssText = `
    text-align: center;
    color: #FFFFFF;
    font-size: 16px;
    font-weight: bold;
    letter-spacing: 3px;
    text-shadow: 2px 2px 0 #3F3F3F, -1px -1px 0 #3F3F3F, 1px -1px 0 #3F3F3F, -1px 1px 0 #3F3F3F;
    margin-bottom: 4px;
  `;
  nameEl.textContent = bossName;
  container.appendChild(nameEl);

  // Bar background
  const barBg = document.createElement('div');
  barBg.style.cssText = `
    width: 100%;
    height: 12px;
    background: rgba(0, 0, 0, 0.6);
    border: 2px solid ${borderColor};
    border-radius: 2px;
    overflow: hidden;
    image-rendering: pixelated;
  `;

  // Bar fill
  const barFill = document.createElement('div');
  barFill.id = 'boss-bar-fill';
  barFill.style.cssText = `
    width: 100%;
    height: 100%;
    background: ${barGradient};
    transition: width 0.3s ease;
  `;
  barBg.appendChild(barFill);
  container.appendChild(barBg);

  // HP text
  const hpText = document.createElement('div');
  hpText.id = 'boss-hp-text';
  hpText.style.cssText = `
    text-align: center;
    color: ${hpColor};
    font-size: 11px;
    margin-top: 2px;
    text-shadow: 1px 1px 0 #222;
  `;
  hpText.textContent = '300 / 300';
  container.appendChild(hpText);

  document.body.appendChild(container);

  return {
    container,
    barFill,
    hpText,
    nameEl,
    show() { container.style.opacity = '1'; },
    hide() { container.style.opacity = '0'; },
    update(hp, maxHp) {
      const pct = Math.max(0, hp / maxHp) * 100;
      barFill.style.width = pct + '%';
      hpText.textContent = `${Math.ceil(hp)} / ${maxHp}`;

      // Change color based on HP phase
      if (hp <= maxHp * 0.5) {
        barFill.style.background = lowGradient;
      } else {
        barFill.style.background = barGradient;
      }
    },
    dispose() {
      if (container.parentNode) container.parentNode.removeChild(container);
    }
  };
}

// ============================================================
// EXPLOSION PARTICLES (visual only)
// ============================================================

function createExplosionParticles(scene, position, radius, count) {
  const particles = [];
  const colors = [0xFF6600, 0xFFAA00, 0xFF3300, 0x888888, 0x444444];

  for (let i = 0; i < count; i++) {
    const color = colors[Math.floor(Math.random() * colors.length)];
    const size = 0.2 + Math.random() * 0.4;
    const geo = new THREE.BoxGeometry(size, size, size);
    const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 1 });
    const mesh = new THREE.Mesh(geo, mat);

    mesh.position.copy(position);

    const velocity = new THREE.Vector3(
      (Math.random() - 0.5) * radius * 2,
      Math.random() * radius * 1.5,
      (Math.random() - 0.5) * radius * 2
    );

    particles.push({ mesh, velocity, life: 0.5 + Math.random() * 0.5 });
    scene.add(mesh);
  }

  return particles;
}

// ============================================================
// BOSS SYSTEM
// ============================================================

export class BossSystem {
  constructor(scene, world, soundManager) {
    this.scene = scene;
    this.world = world;
    this.soundManager = soundManager;

    // Wither state
    this.wither = null;       // { mesh, position, health, maxHealth, phase, ... }
    this.witherSkulls = [];   // active projectiles
    this.healthBar = null;
    this.withersKilled = 0;   // Count to trigger Wither Storm at 3

    // Wither Storm state (mod-style massive boss that absorbs blocks)
    this.witherStorm = null;
    this.witherStormHealthBar = null;

    // Dragon state
    this.dragon = null;       // { mesh, position, health, maxHealth, state, ... }
    this.endCrystals = [];    // { mesh, position, alive, pillarIndex }
    this.dragonHealthBar = null;

    // Explosion particles (visual effects)
    this.particles = [];

    // Screen shake state
    this.screenShake = { active: false, intensity: 0, timer: 0 };

    // Track pending timers for cleanup
    this._pendingTimers = [];

    // Temp vectors
    this._tempVec = new THREE.Vector3();
    this._tempVec2 = new THREE.Vector3();
  }

  // ========================================================
  // CHECK WITHER SUMMON PATTERN
  // ========================================================

  /**
   * Check if placing a SOUL_SAND block completes a T-shape:
   *
   *   S S S     (top row: y+1)
   *     S       (bottom center: y)
   *
   * The placed block is at (x, y, z).
   * We check both possible orientations (along X and along Z).
   */
  checkWitherSummon(x, y, z) {
    if (this.wither) return false; // Boss already active

    const w = this.world;
    const SS = BlockId.SOUL_SAND;

    // Check if the block at position is soul sand
    if (w.getBlock(x, y, z) !== SS) return false;

    // Try all possible T-shape configurations where (x,y,z) is part of the pattern
    // The T-shape is 3 blocks wide on top, 1 block below center

    // Orientation along X-axis (3 blocks wide = x-1, x, x+1 on top row)
    if (this._checkTShapeX(x, y, z, w, SS)) return true;
    if (this._checkTShapeX(x - 1, y, z, w, SS)) return true;
    if (this._checkTShapeX(x + 1, y, z, w, SS)) return true;
    if (this._checkTShapeX(x, y + 1, z, w, SS)) return true;
    if (this._checkTShapeX(x - 1, y + 1, z, w, SS)) return true;
    if (this._checkTShapeX(x + 1, y + 1, z, w, SS)) return true;

    // Orientation along Z-axis (3 blocks wide = z-1, z, z+1 on top row)
    if (this._checkTShapeZ(x, y, z, w, SS)) return true;
    if (this._checkTShapeZ(x, y, z - 1, w, SS)) return true;
    if (this._checkTShapeZ(x, y, z + 1, w, SS)) return true;
    if (this._checkTShapeZ(x, y + 1, z, w, SS)) return true;
    if (this._checkTShapeZ(x, y + 1, z - 1, w, SS)) return true;
    if (this._checkTShapeZ(x, y + 1, z + 1, w, SS)) return true;

    return false;
  }

  // Check T-shape aligned along X: cx is center of top row at (cx, ty, cz), bottom at (cx, ty-1, cz)
  _checkTShapeX(cx, ty, cz, w, SS) {
    // Top row: (cx-1, ty, cz), (cx, ty, cz), (cx+1, ty, cz)
    // Bottom: (cx, ty-1, cz)
    if (w.getBlock(cx - 1, ty, cz) !== SS) return false;
    if (w.getBlock(cx, ty, cz) !== SS) return false;
    if (w.getBlock(cx + 1, ty, cz) !== SS) return false;
    if (w.getBlock(cx, ty - 1, cz) !== SS) return false;

    // Valid T-shape! Spawn wither
    this._spawnWither(cx, ty + 2, cz);

    // Remove the soul sand blocks
    w.setBlock(cx - 1, ty, cz, BlockId.AIR);
    w.setBlock(cx, ty, cz, BlockId.AIR);
    w.setBlock(cx + 1, ty, cz, BlockId.AIR);
    w.setBlock(cx, ty - 1, cz, BlockId.AIR);

    return true;
  }

  // Check T-shape aligned along Z
  _checkTShapeZ(cx, ty, cz, w, SS) {
    // Top row: (cx, ty, cz-1), (cx, ty, cz), (cx, ty, cz+1)
    // Bottom: (cx, ty-1, cz)
    if (w.getBlock(cx, ty, cz - 1) !== SS) return false;
    if (w.getBlock(cx, ty, cz) !== SS) return false;
    if (w.getBlock(cx, ty, cz + 1) !== SS) return false;
    if (w.getBlock(cx, ty - 1, cz) !== SS) return false;

    // Valid T-shape! Spawn wither
    this._spawnWither(cx, ty + 2, cz);

    // Remove the soul sand blocks
    w.setBlock(cx, ty, cz - 1, BlockId.AIR);
    w.setBlock(cx, ty, cz, BlockId.AIR);
    w.setBlock(cx, ty, cz + 1, BlockId.AIR);
    w.setBlock(cx, ty - 1, cz, BlockId.AIR);

    return true;
  }

  // ========================================================
  // SPAWN WITHER
  // ========================================================

  _spawnWither(x, y, z) {
    const { group, heads } = buildWitherMesh();
    group.position.set(x, y, z);
    this.scene.add(group);

    this.wither = {
      mesh: group,
      heads,
      position: new THREE.Vector3(x, y, z),
      health: 300,
      maxHealth: 300,
      phase: 1,
      attackTimer: 3.0,          // Initial delay before first attack
      orbitAngle: 0,
      orbitRadius: 15,
      targetHeight: y + 8,
      chargeTimer: 0,
      isCharging: false,
      chargeTarget: null,
      animTime: 0,
      invulnTimer: 1.5,         // Brief invulnerability on spawn
      hurtTimer: 0,             // Flash red on damage
      deathTimer: -1,           // -1 = not dying
    };

    // Create health bar
    this.healthBar = createBossHealthBar();
    this.healthBar.show();
    this.healthBar.update(300, 300);

    // Wither spawn cinematic callback
    if (this.onWitherSpawn) this.onWitherSpawn();

    // Spawn explosion: destroy blocks in radius and visual effect
    this._spawnExplosion(x, y, z, 3);

    // Screen shake
    this.screenShake = { active: true, intensity: 0.5, timer: 1.5 };

    // Play explosion sound
    if (this.soundManager && this.soundManager.playExplosion) {
      this.soundManager.playExplosion(
        new THREE.Vector3(x, y, z),
        new THREE.Vector3(x, y, z)
      );
    }
  }

  _spawnExplosion(x, y, z, radius) {
    // Destroy blocks in radius
    const explosions = [];
    for (let dx = -radius; dx <= radius; dx++) {
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dz = -radius; dz <= radius; dz++) {
          if (dx * dx + dy * dy + dz * dz <= radius * radius) {
            const bx = Math.floor(x) + dx;
            const by = Math.floor(y) + dy;
            const bz = Math.floor(z) + dz;
            const block = this.world.getBlock(bx, by, bz);
            if (block !== BlockId.AIR && block !== BlockId.BEDROCK && block !== BlockId.WATER) {
              this.world.setBlock(bx, by, bz, BlockId.AIR);
              explosions.push({ x: bx, y: by, z: bz });
            }
          }
        }
      }
    }

    // Visual particles
    const newParticles = createExplosionParticles(
      this.scene,
      new THREE.Vector3(x, y, z),
      radius,
      30
    );
    this.particles.push(...newParticles);

    return explosions;
  }

  // ========================================================
  // UPDATE
  // ========================================================

  update(dt, playerPos, camera) {
    const result = { damage: 0, explosions: [] };

    // Update dragon if active (mutually exclusive with wither - different dimensions)
    if (this.dragon) {
      const dragonResult = this._updateDragon(dt, playerPos, camera);
      result.damage += dragonResult.damage;
      result.explosions.push(...dragonResult.explosions);
      this._updateParticles(dt);
      this._updateScreenShake(dt, camera);
      return result;
    }

    // Update Wither Storm (independent of regular wither)
    if (this.witherStorm) {
      const wsResult = this._updateWitherStorm(dt, playerPos);
      result.damage += wsResult.damage;
      result.explosions.push(...wsResult.explosions);
    }

    if (!this.wither) {
      this._updateParticles(dt);
      this._updateScreenShake(dt, camera);
      return result;
    }

    const w = this.wither;
    w.animTime += dt;

    // Invulnerability countdown (on spawn)
    if (w.invulnTimer > 0) {
      w.invulnTimer -= dt;
    }

    // Hurt flash timer
    if (w.hurtTimer > 0) {
      w.hurtTimer -= dt;
      // Flash the mesh
      if (w.mesh) {
        const flash = Math.sin(w.hurtTimer * 30) > 0;
        w.mesh.traverse(child => {
          if (child.isMesh && !child.userData.isAuraParticle) {
            if (child.material && !child.material._origColor) {
              child.material._origColor = child.material.color.getHex();
            }
            if (flash && child.material) {
              child.material.color.setHex(0xFF4444);
            } else if (child.material && child.material._origColor) {
              child.material.color.setHex(child.material._origColor);
            }
          }
        });
        if (w.hurtTimer <= 0) {
          // Restore all colors
          w.mesh.traverse(child => {
            if (child.isMesh && child.material && child.material._origColor) {
              child.material.color.setHex(child.material._origColor);
              delete child.material._origColor;
            }
          });
        }
      }
    }

    // Death sequence
    if (w.deathTimer >= 0) {
      w.deathTimer -= dt;
      // Shake and flash during death
      if (w.mesh) {
        w.mesh.rotation.z = Math.sin(w.animTime * 20) * 0.2;
        w.mesh.position.y = w.position.y + Math.sin(w.animTime * 10) * 0.3;
      }
      if (w.deathTimer <= 0) {
        this._witherDeath();
      }
      this._updateParticles(dt);
      this._updateScreenShake(dt, camera);
      return result;
    }

    // Determine phase
    const oldPhase = w.phase;
    w.phase = w.health > w.maxHealth * 0.5 ? 1 : 2;
    if (oldPhase === 1 && w.phase === 2) {
      // Phase transition: explosion + scream
      this._spawnExplosion(w.position.x, w.position.y, w.position.z, 2);
      this.screenShake = { active: true, intensity: 0.3, timer: 1.0 };
    }

    // AI: Orbit player
    const attackInterval = w.phase === 1 ? 2.0 : 1.0;
    const orbitSpeed = w.phase === 1 ? 0.5 : 0.8;
    const targetRadius = w.phase === 1 ? 15 : 10;

    // Smooth orbit radius adjustment
    w.orbitRadius += (targetRadius - w.orbitRadius) * dt * 2;
    w.orbitAngle += orbitSpeed * dt;

    // Calculate target position (orbit around player)
    const targetX = playerPos.x + Math.cos(w.orbitAngle) * w.orbitRadius;
    const targetZ = playerPos.z + Math.sin(w.orbitAngle) * w.orbitRadius;
    const targetY = playerPos.y + 8 + Math.sin(w.animTime * 0.7) * 2; // Bob up and down

    // Charge attack (occasional dive toward player)
    if (!w.isCharging) {
      w.chargeTimer -= dt;
      if (w.chargeTimer <= 0) {
        // Random chance to charge
        if (Math.random() < (w.phase === 1 ? 0.3 : 0.5)) {
          w.isCharging = true;
          w.chargeTarget = playerPos.clone();
          w.chargeTarget.y += 2; // Aim at player head height
        }
        w.chargeTimer = 3 + Math.random() * 2;
      }
    }

    // Move toward target
    let moveTargetX, moveTargetY, moveTargetZ;
    if (w.isCharging && w.chargeTarget) {
      moveTargetX = w.chargeTarget.x;
      moveTargetY = w.chargeTarget.y;
      moveTargetZ = w.chargeTarget.z;

      // Check if close enough to stop charging
      const chargeDist = w.position.distanceTo(w.chargeTarget);
      if (chargeDist < 3) {
        w.isCharging = false;
        w.chargeTarget = null;
      }
    } else {
      moveTargetX = targetX;
      moveTargetY = targetY;
      moveTargetZ = targetZ;
    }

    const moveSpeed = w.isCharging ? 12 : (w.phase === 1 ? 5 : 7);
    this._tempVec.set(
      moveTargetX - w.position.x,
      moveTargetY - w.position.y,
      moveTargetZ - w.position.z
    );
    const dist = this._tempVec.length();
    if (dist > 0.1) {
      this._tempVec.normalize().multiplyScalar(Math.min(moveSpeed * dt, dist));
      w.position.add(this._tempVec);
    }

    // Face the player
    if (w.mesh) {
      const dx = playerPos.x - w.position.x;
      const dz = playerPos.z - w.position.z;
      w.mesh.rotation.y = Math.atan2(dx, dz);
      w.mesh.position.copy(w.position);

      // Animate heads (slight rotation)
      for (let i = 0; i < w.heads.length; i++) {
        const head = w.heads[i];
        head.rotation.y = Math.sin(w.animTime * 1.5 + i * 2.1) * 0.3;
        head.rotation.x = Math.sin(w.animTime * 1.0 + i * 1.3) * 0.15;
      }

      // Animate aura particles
      w.mesh.traverse(child => {
        if (child.userData.isAuraParticle) {
          child.position.y += Math.sin(w.animTime * 3 + child.position.x * 5) * 0.02;
          child.position.x += Math.cos(w.animTime * 2 + child.position.z * 3) * 0.01;
        }
      });
    }

    // Attack: shoot skulls
    w.attackTimer -= dt;
    if (w.attackTimer <= 0) {
      this._shootSkull(playerPos);
      w.attackTimer = attackInterval;

      // In phase 2, occasionally shoot triple skulls
      if (w.phase === 2 && Math.random() < 0.4) {
        const t1 = setTimeout(() => {
          if (this.wither) this._shootSkull(playerPos);
        }, 200);
        const t2 = setTimeout(() => {
          if (this.wither) this._shootSkull(playerPos);
        }, 400);
        this._pendingTimers.push(t1, t2);
      }
    }

    // Update skull projectiles
    const skullResult = this._updateSkulls(dt, playerPos);
    result.damage += skullResult.damage;
    result.explosions.push(...skullResult.explosions);

    // Update health bar
    if (this.healthBar) {
      this.healthBar.update(w.health, w.maxHealth);
    }

    // Update visual particles
    this._updateParticles(dt);

    // Update screen shake
    this._updateScreenShake(dt, camera);

    return result;
  }

  // ========================================================
  // SKULL ATTACKS
  // ========================================================

  _shootSkull(playerPos) {
    if (!this.wither) return;

    const skull = createSkullProjectile();
    const startPos = this.wither.position.clone();
    startPos.y += 1; // Shoot from head height

    skull.position.copy(startPos);
    this.scene.add(skull);

    // Direction toward player with slight spread
    const dir = new THREE.Vector3()
      .subVectors(playerPos, startPos)
      .normalize();
    dir.x += (Math.random() - 0.5) * 0.15;
    dir.z += (Math.random() - 0.5) * 0.15;
    dir.normalize();

    const speed = this.wither.phase === 1 ? 15 : 20;

    this.witherSkulls.push({
      mesh: skull,
      position: startPos.clone(),
      velocity: dir.multiplyScalar(speed),
      life: 5.0,  // Max lifetime in seconds
    });

    // Sound effect
    if (this.soundManager && this.soundManager.playHostileSound) {
      this.soundManager.playHostileSound('GHAST', playerPos, startPos);
    }
  }

  _updateSkulls(dt, playerPos) {
    const result = { damage: 0, explosions: [] };

    for (let i = this.witherSkulls.length - 1; i >= 0; i--) {
      const skull = this.witherSkulls[i];
      skull.life -= dt;

      // Move
      skull.position.x += skull.velocity.x * dt;
      skull.position.y += skull.velocity.y * dt;
      skull.position.z += skull.velocity.z * dt;

      if (skull.mesh) {
        skull.mesh.position.copy(skull.position);
        skull.mesh.rotation.y += dt * 5; // Spin
      }

      // Check collision with player (2-block radius)
      const distToPlayer = skull.position.distanceTo(playerPos);
      if (distToPlayer < 2.0) {
        // Hit player!
        result.damage += 8;
        const expl = this._skullExplosion(skull.position);
        result.explosions.push(...expl);
        this._removeSkull(i);
        continue;
      }

      // Check collision with terrain
      const bx = Math.floor(skull.position.x);
      const by = Math.floor(skull.position.y);
      const bz = Math.floor(skull.position.z);
      const block = this.world.getBlock(bx, by, bz);
      if (block !== BlockId.AIR && block !== BlockId.WATER) {
        const expl = this._skullExplosion(skull.position);
        result.explosions.push(...expl);
        this._removeSkull(i);
        continue;
      }

      // Lifetime expired
      if (skull.life <= 0) {
        this._removeSkull(i);
        continue;
      }
    }

    return result;
  }

  _skullExplosion(position) {
    const radius = 3;
    const destroyed = [];

    // Destroy blocks in radius
    for (let dx = -radius; dx <= radius; dx++) {
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dz = -radius; dz <= radius; dz++) {
          if (dx * dx + dy * dy + dz * dz <= radius * radius) {
            const bx = Math.floor(position.x) + dx;
            const by = Math.floor(position.y) + dy;
            const bz = Math.floor(position.z) + dz;
            const block = this.world.getBlock(bx, by, bz);
            if (block !== BlockId.AIR && block !== BlockId.BEDROCK && block !== BlockId.WATER) {
              this.world.setBlock(bx, by, bz, BlockId.AIR);
              destroyed.push({ x: bx, y: by, z: bz });
            }
          }
        }
      }
    }

    // Visual particles
    const newParticles = createExplosionParticles(this.scene, position, radius, 20);
    this.particles.push(...newParticles);

    // Screen shake
    this.screenShake = { active: true, intensity: 0.15, timer: 0.3 };

    // Sound
    if (this.soundManager && this.soundManager.playExplosion) {
      this.soundManager.playExplosion(position, position);
    }

    return destroyed;
  }

  _removeSkull(index) {
    const skull = this.witherSkulls[index];
    if (skull.mesh) {
      this.scene.remove(skull.mesh);
      skull.mesh.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(m => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
    }
    this.witherSkulls.splice(index, 1);
  }

  // ========================================================
  // DAMAGE WITHER
  // ========================================================

  damageWither(amount) {
    if (!this.wither) return { dead: false, drops: [] };
    if (this.wither.invulnTimer > 0) return { dead: false, drops: [] };
    if (this.wither.deathTimer >= 0) return { dead: false, drops: [] };

    this.wither.health -= amount;
    this.wither.hurtTimer = 0.3; // Flash red

    // Knockback: push wither away slightly
    // (not critical, just visual feedback)

    if (this.wither.health <= 0) {
      this.wither.health = 0;
      this.wither.deathTimer = 2.0; // 2-second death animation
      this.screenShake = { active: true, intensity: 0.4, timer: 2.0 };

      if (this.healthBar) {
        this.healthBar.update(0, this.wither.maxHealth);
      }

      return { dead: true, drops: [] }; // Drops given after death animation
    }

    if (this.healthBar) {
      this.healthBar.update(this.wither.health, this.wither.maxHealth);
    }

    return { dead: false, drops: [] };
  }

  // ========================================================
  // WITHER DEATH
  // ========================================================

  _witherDeath() {
    if (!this.wither) return;

    const pos = this.wither.position.clone();

    // Big explosion effect
    this._spawnExplosion(pos.x, pos.y, pos.z, 4);
    createExplosionParticles(this.scene, pos, 6, 50).forEach(p => this.particles.push(p));

    // Screen shake
    this.screenShake = { active: true, intensity: 0.6, timer: 2.0 };

    // Sound
    if (this.soundManager && this.soundManager.playExplosion) {
      this.soundManager.playExplosion(pos, pos);
    }

    // Remove wither mesh
    if (this.wither.mesh) {
      this.scene.remove(this.wither.mesh);
      this.wither.mesh.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(m => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
    }

    // Remove remaining skulls
    for (let i = this.witherSkulls.length - 1; i >= 0; i--) {
      this._removeSkull(i);
    }

    // Clear any pending attack timers
    for (const t of this._pendingTimers) clearTimeout(t);
    this._pendingTimers = [];

    // Hide health bar
    if (this.healthBar) {
      this.healthBar.hide();
      const t = setTimeout(() => {
        if (this.healthBar) {
          this.healthBar.dispose();
          this.healthBar = null;
        }
      }, 1000);
      this._pendingTimers.push(t);
    }

    // Store drop info before clearing wither
    this.wither = null;
    this.withersKilled++;

    // The drops are returned via getDeathDrops() which main.js can call
    this._pendingDrops = {
      position: pos,
      items: [
        { itemId: ItemId.NETHER_STAR, count: 1, isBlock: false },
      ],
      xp: 50,
    };

    // After 3 withers killed → unleash the Wither Storm
    if (this.withersKilled >= 3 && !this.witherStorm) {
      const t = setTimeout(() => {
        if (this._pendingTimers && !this.witherStorm) {
          this.spawnWitherStorm(pos.x, pos.y + 20, pos.z);
        }
      }, 4000);
      this._pendingTimers.push(t);
    }
  }

  // ========================================================
  // WITHER STORM (mod-style mega boss that absorbs blocks)
  // ========================================================

  spawnWitherStorm(x, y, z) {
    if (this.witherStorm) return;

    // Reuse wither mesh but scale up ~2.8x and tint darker
    const { group, heads } = buildWitherMesh();
    group.scale.setScalar(2.8);
    // Tint all materials darker purple
    group.traverse(child => {
      if (child.material && child.material.color) {
        const c = child.material.color;
        c.r *= 0.4; c.g *= 0.3; c.b *= 0.5;
      }
    });
    group.position.set(x, y, z);
    this.scene.add(group);

    // Add a glowing dark aura sphere
    const auraGeo = new THREE.SphereGeometry(8, 16, 12);
    const auraMat = new THREE.MeshBasicMaterial({
      color: 0x6A1B9A, transparent: true, opacity: 0.15, side: THREE.BackSide,
    });
    const aura = new THREE.Mesh(auraGeo, auraMat);
    group.add(aura);

    this.witherStorm = {
      mesh: group,
      heads,
      aura,
      position: new THREE.Vector3(x, y, z),
      health: 1000,
      maxHealth: 1000,
      animTime: 0,
      absorbTimer: 0,
      attackTimer: 4.0,
      hurtTimer: 0,
      deathTimer: -1,
      invulnTimer: 2.0,
    };

    // Big health bar
    this.witherStormHealthBar = createBossHealthBar();
    this.witherStormHealthBar.show();
    this.witherStormHealthBar.update(1000, 1000);

    // Dramatic explosion + shake
    this._spawnExplosion(x, y, z, 5);
    this.screenShake = { active: true, intensity: 0.9, timer: 3.0 };

    if (this.soundManager && this.soundManager.playExplosion) {
      this.soundManager.playExplosion(
        new THREE.Vector3(x, y, z),
        new THREE.Vector3(x, y, z)
      );
    }

    if (this.onWitherStormSpawn) this.onWitherStormSpawn();
  }

  _updateWitherStorm(dt, playerPos) {
    const result = { damage: 0, explosions: [] };
    if (!this.witherStorm) return result;
    const ws = this.witherStorm;

    ws.animTime += dt;
    ws.hurtTimer = Math.max(0, ws.hurtTimer - dt);
    ws.invulnTimer = Math.max(0, ws.invulnTimer - dt);

    // Death animation
    if (ws.deathTimer > 0) {
      ws.deathTimer -= dt;
      if (ws.mesh) ws.mesh.rotation.y += dt * 4;
      if (ws.deathTimer <= 0) this._witherStormDeath();
      return result;
    }

    // Drift slowly toward player (hover at ~15 above ground)
    const dx = playerPos.x - ws.position.x;
    const dz = playerPos.z - ws.position.z;
    const d = Math.hypot(dx, dz) || 1;
    if (d > 15) {
      ws.position.x += (dx / d) * 1.8 * dt;
      ws.position.z += (dz / d) * 1.8 * dt;
    }
    // Bob up & down
    ws.position.y += Math.sin(ws.animTime * 0.7) * 0.04;

    if (ws.mesh) {
      ws.mesh.position.copy(ws.position);
      // Face the player
      ws.mesh.rotation.y = Math.atan2(dx, dz);
    }

    // Aura pulse
    if (ws.aura) {
      ws.aura.material.opacity = 0.12 + Math.sin(ws.animTime * 2.0) * 0.04;
    }

    // ABSORB blocks every 1.2s — deletes blocks in radius and adds HP
    ws.absorbTimer -= dt;
    if (ws.absorbTimer <= 0) {
      ws.absorbTimer = 1.2;
      const radius = 7;
      const cx = Math.floor(ws.position.x);
      const cy = Math.floor(ws.position.y);
      const cz = Math.floor(ws.position.z);
      let absorbed = 0;
      for (let i = 0; i < 12; i++) {
        const ax = cx + Math.floor((Math.random() - 0.5) * radius * 2);
        const ay = cy + Math.floor((Math.random() - 0.5) * radius * 2);
        const az = cz + Math.floor((Math.random() - 0.5) * radius * 2);
        const block = this.world.getBlock(ax, ay, az);
        if (block !== BlockId.AIR && block !== BlockId.BEDROCK && block !== BlockId.WATER) {
          this.world.setBlock(ax, ay, az, BlockId.AIR);
          absorbed++;
          if (absorbed >= 8) break;
        }
      }
      if (absorbed > 0 && ws.health < ws.maxHealth) {
        ws.health = Math.min(ws.maxHealth, ws.health + absorbed);
        if (this.witherStormHealthBar) {
          this.witherStormHealthBar.update(ws.health, ws.maxHealth);
        }
      }
    }

    // Attack: contact damage if player is too close
    const distToPlayer = ws.position.distanceTo(playerPos);
    if (distToPlayer < 6) {
      ws.attackTimer -= dt;
      if (ws.attackTimer <= 0) {
        result.damage += 6;
        ws.attackTimer = 1.5;
      }
    } else {
      ws.attackTimer = Math.max(0, ws.attackTimer - dt);
    }

    return result;
  }

  damageWitherStorm(amount) {
    if (!this.witherStorm) return { dead: false };
    if (this.witherStorm.invulnTimer > 0) return { dead: false };
    if (this.witherStorm.deathTimer > 0) return { dead: false };

    this.witherStorm.health -= amount;
    this.witherStorm.hurtTimer = 0.3;

    if (this.witherStorm.health <= 0) {
      this.witherStorm.health = 0;
      this.witherStorm.deathTimer = 3.0;
      this.screenShake = { active: true, intensity: 0.8, timer: 3.0 };
      return { dead: true };
    }
    if (this.witherStormHealthBar) {
      this.witherStormHealthBar.update(this.witherStorm.health, this.witherStorm.maxHealth);
    }
    return { dead: false };
  }

  _witherStormDeath() {
    if (!this.witherStorm) return;
    const pos = this.witherStorm.position.clone();

    // Massive explosion
    this._spawnExplosion(pos.x, pos.y, pos.z, 8);
    createExplosionParticles(this.scene, pos, 12, 100).forEach(p => this.particles.push(p));
    this.screenShake = { active: true, intensity: 1.0, timer: 3.0 };

    if (this.witherStorm.mesh) {
      this.scene.remove(this.witherStorm.mesh);
      this.witherStorm.mesh.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) child.material.forEach(m => m.dispose());
          else child.material.dispose();
        }
      });
    }

    if (this.witherStormHealthBar) {
      this.witherStormHealthBar.hide();
      const t = setTimeout(() => {
        if (this.witherStormHealthBar) {
          this.witherStormHealthBar.dispose();
          this.witherStormHealthBar = null;
        }
      }, 1500);
      this._pendingTimers.push(t);
    }

    this.witherStorm = null;
    this.withersKilled = 0; // Reset

    // Massive drops
    this._pendingDrops = {
      position: pos,
      items: [
        { itemId: ItemId.NETHER_STAR, count: 5, isBlock: false },
      ],
      xp: 500,
    };
  }

  /**
   * Get pending death drops (call after update if boss just died).
   * Returns null if no drops pending.
   */
  getDeathDrops() {
    if (this._pendingDrops) {
      const drops = this._pendingDrops;
      this._pendingDrops = null;
      return drops;
    }
    return null;
  }

  // ========================================================
  // SPAWN DRAGON
  // ========================================================

  spawnDragon(x, y, z, pillarPositions) {
    if (this.dragon) return; // Already active

    const { group, headGroup, wingL, wingR, tailSegments } = buildDragonMesh();
    group.position.set(x, y, z);
    this.scene.add(group);

    this.dragon = {
      mesh: group,
      headGroup,
      wingL,
      wingR,
      tailSegments,
      position: new THREE.Vector3(x, y, z),
      health: 200,
      maxHealth: 200,
      state: 'CIRCLING',       // CIRCLING | STRAFING | PERCHING
      orbitAngle: 0,
      orbitRadius: 50,
      animTime: 0,
      strafingTimer: 15.0,     // Time until next strafe
      perchingTimer: 60.0,     // Time until next perch
      perchDuration: 0,        // How long perched so far
      strafingTarget: null,
      strafingPassed: false,
      invulnTimer: 1.5,
      hurtTimer: 0,
      deathTimer: -1,
      breathTimer: 0,          // AOE breath attack cooldown while perching
    };

    // Create end crystals on pillars
    this.endCrystals = [];
    const pillars = pillarPositions || [];
    for (let i = 0; i < pillars.length; i++) {
      const p = pillars[i];
      const crystalMesh = buildCrystalMesh();
      // Crystal sits on top of the pillar
      const cy = 48 + p.height + 1; // approx surface + pillar height + 1
      crystalMesh.position.set(p.x, cy, p.z);
      this.scene.add(crystalMesh);

      this.endCrystals.push({
        mesh: crystalMesh,
        position: new THREE.Vector3(p.x, cy, p.z),
        alive: true,
        pillarIndex: i,
      });
    }

    // Create health bar with purple dragon theme
    this.dragonHealthBar = createBossHealthBar(
      'DRAG\u00D3N DEL END',
      'linear-gradient(to bottom, #8E44AD, #6C3483)',
      'linear-gradient(to bottom, #DD4444, #AA2222)',
      '#CC88EE',
      'rgba(60, 0, 80, 0.8)'
    );
    this.dragonHealthBar.show();
    this.dragonHealthBar.update(200, 200);

    // Spawn effect
    this.screenShake = { active: true, intensity: 0.4, timer: 1.5 };

    if (this.soundManager && this.soundManager.playExplosion) {
      this.soundManager.playExplosion(
        new THREE.Vector3(x, y, z),
        new THREE.Vector3(x, y, z)
      );
    }
  }

  // ========================================================
  // UPDATE DRAGON
  // ========================================================

  _updateDragon(dt, playerPos, camera) {
    const result = { damage: 0, explosions: [] };
    if (!this.dragon) return result;

    const d = this.dragon;
    d.animTime += dt;

    // Invulnerability countdown
    if (d.invulnTimer > 0) {
      d.invulnTimer -= dt;
    }

    // Hurt flash timer
    if (d.hurtTimer > 0) {
      d.hurtTimer -= dt;
      if (d.mesh) {
        const flash = Math.sin(d.hurtTimer * 30) > 0;
        d.mesh.traverse(child => {
          if (child.isMesh && !child.userData.isAuraParticle) {
            if (child.material && !child.material._origColor) {
              child.material._origColor = child.material.color.getHex();
            }
            if (flash && child.material) {
              child.material.color.setHex(0xFF4444);
            } else if (child.material && child.material._origColor) {
              child.material.color.setHex(child.material._origColor);
            }
          }
        });
        if (d.hurtTimer <= 0) {
          d.mesh.traverse(child => {
            if (child.isMesh && child.material && child.material._origColor) {
              child.material.color.setHex(child.material._origColor);
              delete child.material._origColor;
            }
          });
        }
      }
    }

    // Death sequence
    if (d.deathTimer >= 0) {
      d.deathTimer -= dt;
      if (d.mesh) {
        d.mesh.rotation.z = Math.sin(d.animTime * 20) * 0.3;
        d.mesh.position.y = d.position.y + Math.sin(d.animTime * 8) * 0.5;
      }
      if (d.deathTimer <= 0) {
        this._dragonDeath();
      }
      return result;
    }

    // Crystal healing: each alive crystal heals 1 HP/s
    const aliveCrystals = this.endCrystals.filter(c => c.alive).length;
    if (aliveCrystals > 0 && d.health < d.maxHealth) {
      d.health = Math.min(d.maxHealth, d.health + aliveCrystals * dt);
    }

    // Rotate crystal meshes
    for (const crystal of this.endCrystals) {
      if (crystal.alive && crystal.mesh) {
        crystal.mesh.rotation.y += dt * 2;
        crystal.mesh.rotation.x += dt * 1.3;
      }
    }

    // State machine
    d.strafingTimer -= dt;
    d.perchingTimer -= dt;

    if (d.state === 'CIRCLING') {
      // Orbit at Y=70, radius 50 from origin, sinusoidal bobbing
      d.orbitAngle += 0.3 * dt;
      const targetX = Math.cos(d.orbitAngle) * d.orbitRadius;
      const targetZ = Math.sin(d.orbitAngle) * d.orbitRadius;
      const targetY = 70 + Math.sin(d.animTime * 0.8) * 3;

      this._moveDragonToward(d, targetX, targetY, targetZ, 12, dt);

      // Transition: strafe every 15s
      if (d.strafingTimer <= 0) {
        d.state = 'STRAFING';
        d.strafingTarget = playerPos.clone();
        d.strafingTarget.y += 2;
        d.strafingPassed = false;
        d.strafingTimer = 15.0;
      }

      // Transition: perch every 60s
      if (d.perchingTimer <= 0) {
        d.state = 'PERCHING';
        d.perchDuration = 0;
        d.breathTimer = 0;
        d.perchingTimer = 60.0;
      }

    } else if (d.state === 'STRAFING') {
      // Dive toward player
      if (d.strafingTarget) {
        this._moveDragonToward(d, d.strafingTarget.x, d.strafingTarget.y, d.strafingTarget.z, 20, dt);

        const distToTarget = d.position.distanceTo(d.strafingTarget);

        // Contact damage when close to player
        const distToPlayer = d.position.distanceTo(playerPos);
        if (distToPlayer < 4.0 && !d.strafingPassed) {
          result.damage += 10;
          d.strafingPassed = true;
          this.screenShake = { active: true, intensity: 0.25, timer: 0.4 };
        }

        // Passed target or close enough -> return to circling
        if (distToTarget < 3.0 || d.strafingPassed) {
          d.state = 'CIRCLING';
          d.strafingTarget = null;
        }
      } else {
        d.state = 'CIRCLING';
      }

    } else if (d.state === 'PERCHING') {
      // Land at (0, 49, 0)
      const perchX = 0, perchY = 49, perchZ = 0;
      this._moveDragonToward(d, perchX, perchY, perchZ, 10, dt);

      d.perchDuration += dt;

      // Breath attack: AOE damage 3/s in radius 5
      d.breathTimer += dt;
      if (d.breathTimer >= 0.5) {
        d.breathTimer = 0;
        const distToPlayer = d.position.distanceTo(playerPos);
        if (distToPlayer < 5.0) {
          result.damage += 1.5; // 3/s applied every 0.5s
          // Breath particles
          const breathParticles = createExplosionParticles(
            this.scene,
            d.position.clone().add(new THREE.Vector3(0, 0, 3)),
            3, 5
          );
          // Tint breath particles purple
          for (const bp of breathParticles) {
            bp.mesh.material.color.setHex(0x8E44AD);
          }
          this.particles.push(...breathParticles);
        }
      }

      // After 8 seconds, return to circling
      if (d.perchDuration >= 8) {
        d.state = 'CIRCLING';
      }
    }

    // Face movement direction
    if (d.mesh) {
      const dx = d.mesh.position.x - d.position.x;
      const dz = d.mesh.position.z - d.position.z;
      // Face forward based on orbit angle
      d.mesh.rotation.y = d.orbitAngle + Math.PI;
      d.mesh.position.copy(d.position);

      // Wing flap animation
      if (d.wingL) d.wingL.rotation.z = Math.sin(d.animTime * 4) * 0.4;
      if (d.wingR) d.wingR.rotation.z = -Math.sin(d.animTime * 4) * 0.4;

      // Tail sway
      for (let i = 0; i < d.tailSegments.length; i++) {
        d.tailSegments[i].rotation.y = Math.sin(d.animTime * 2 + i * 0.5) * 0.15;
      }

      // Head bob
      if (d.headGroup) {
        d.headGroup.rotation.x = Math.sin(d.animTime * 1.5) * 0.1;
      }

      // Aura particles
      d.mesh.traverse(child => {
        if (child.userData.isAuraParticle) {
          child.position.y += Math.sin(d.animTime * 3 + child.position.x * 5) * 0.02;
          child.position.x += Math.cos(d.animTime * 2 + child.position.z * 3) * 0.01;
        }
      });
    }

    // Update health bar
    if (this.dragonHealthBar) {
      this.dragonHealthBar.update(d.health, d.maxHealth);
    }

    return result;
  }

  _moveDragonToward(d, tx, ty, tz, speed, dt) {
    this._tempVec.set(tx - d.position.x, ty - d.position.y, tz - d.position.z);
    const dist = this._tempVec.length();
    if (dist > 0.1) {
      this._tempVec.normalize().multiplyScalar(Math.min(speed * dt, dist));
      d.position.add(this._tempVec);
    }
  }

  // ========================================================
  // DAMAGE DRAGON
  // ========================================================

  damageDragon(amount) {
    if (!this.dragon) return { dead: false, drops: [] };
    if (this.dragon.invulnTimer > 0) return { dead: false, drops: [] };
    if (this.dragon.deathTimer >= 0) return { dead: false, drops: [] };

    this.dragon.health -= amount;
    this.dragon.hurtTimer = 0.3;

    if (this.dragon.health <= 0) {
      this.dragon.health = 0;
      this.dragon.deathTimer = 3.0; // 3-second death animation (bigger boss)
      this.screenShake = { active: true, intensity: 0.5, timer: 3.0 };

      if (this.dragonHealthBar) {
        this.dragonHealthBar.update(0, this.dragon.maxHealth);
      }

      return { dead: true, drops: [] };
    }

    if (this.dragonHealthBar) {
      this.dragonHealthBar.update(this.dragon.health, this.dragon.maxHealth);
    }

    return { dead: false, drops: [] };
  }

  // ========================================================
  // HIT END CRYSTAL
  // ========================================================

  hitCrystal(index) {
    if (index < 0 || index >= this.endCrystals.length) return null;
    const crystal = this.endCrystals[index];
    if (!crystal.alive) return null;

    crystal.alive = false;
    const pos = crystal.position.clone();

    // Remove crystal mesh
    if (crystal.mesh) {
      this.scene.remove(crystal.mesh);
      crystal.mesh.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(m => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
      crystal.mesh = null;
    }

    // Explosion particles
    const newParticles = createExplosionParticles(this.scene, pos, 4, 25);
    // Tint pink
    for (const p of newParticles) {
      p.mesh.material.color.setHex(0xE74C8B);
    }
    this.particles.push(...newParticles);

    // Screen shake
    this.screenShake = { active: true, intensity: 0.2, timer: 0.5 };

    // Sound
    if (this.soundManager && this.soundManager.playExplosion) {
      this.soundManager.playExplosion(pos, pos);
    }

    return { position: pos, damageRadius: 6, damage: 6 };
  }

  // ========================================================
  // DRAGON DEATH
  // ========================================================

  _dragonDeath() {
    if (!this.dragon) return;

    const pos = this.dragon.position.clone();

    // Big explosion effect (multiple)
    for (let i = 0; i < 3; i++) {
      const offset = new THREE.Vector3(
        (Math.random() - 0.5) * 6,
        (Math.random() - 0.5) * 4,
        (Math.random() - 0.5) * 6
      );
      const ePos = pos.clone().add(offset);
      createExplosionParticles(this.scene, ePos, 5, 30).forEach(p => this.particles.push(p));
    }

    // Screen shake
    this.screenShake = { active: true, intensity: 0.7, timer: 2.5 };

    // Sound
    if (this.soundManager && this.soundManager.playExplosion) {
      this.soundManager.playExplosion(pos, pos);
    }

    // Remove dragon mesh
    if (this.dragon.mesh) {
      this.scene.remove(this.dragon.mesh);
      this.dragon.mesh.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(m => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
    }

    // Remove remaining crystals
    for (let i = 0; i < this.endCrystals.length; i++) {
      const crystal = this.endCrystals[i];
      if (crystal.mesh) {
        this.scene.remove(crystal.mesh);
        crystal.mesh.traverse(child => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(m => m.dispose());
            } else {
              child.material.dispose();
            }
          }
        });
      }
    }
    this.endCrystals = [];

    // Hide health bar
    if (this.dragonHealthBar) {
      this.dragonHealthBar.hide();
      const t = setTimeout(() => {
        if (this.dragonHealthBar) {
          this.dragonHealthBar.dispose();
          this.dragonHealthBar = null;
        }
      }, 1000);
      this._pendingTimers.push(t);
    }

    // Store drop info before clearing dragon
    this.dragon = null;

    this._pendingDrops = {
      position: pos,
      items: [
        { itemId: ItemId.DRAGON_BREATH, count: 1, isBlock: false },
      ],
      xp: 12000,
    };
  }

  // ========================================================
  // DRAGON PUBLIC GETTERS
  // ========================================================

  isDragonAlive() {
    return this.dragon !== null;
  }

  getDragonPosition() {
    if (!this.dragon) return null;
    return this.dragon.position.clone();
  }

  // ========================================================
  // PARTICLES & SCREEN SHAKE
  // ========================================================

  _updateParticles(dt) {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life -= dt;

      if (p.life <= 0) {
        this.scene.remove(p.mesh);
        p.mesh.geometry.dispose();
        p.mesh.material.dispose();
        this.particles.splice(i, 1);
        continue;
      }

      // Move
      p.mesh.position.x += p.velocity.x * dt;
      p.mesh.position.y += p.velocity.y * dt;
      p.mesh.position.z += p.velocity.z * dt;

      // Gravity
      p.velocity.y -= 10 * dt;

      // Fade
      p.mesh.material.opacity = Math.max(0, p.life);

      // Shrink
      const scale = Math.max(0.1, p.life);
      p.mesh.scale.setScalar(scale);
    }
  }

  _updateScreenShake(dt, camera) {
    if (!this.screenShake.active || !camera) return;

    this.screenShake.timer -= dt;
    if (this.screenShake.timer <= 0) {
      this.screenShake.active = false;
      return;
    }

    const intensity = this.screenShake.intensity * (this.screenShake.timer / 1.0);
    camera.position.x += (Math.random() - 0.5) * intensity;
    camera.position.y += (Math.random() - 0.5) * intensity;
    camera.position.z += (Math.random() - 0.5) * intensity;
  }

  // ========================================================
  // PUBLIC API
  // ========================================================

  /** Check if boss is currently active */
  isActive() {
    return this.wither !== null || this.dragon !== null;
  }

  /** Get boss entities for hit detection (e.g. arrow collisions) */
  getBossEntities() {
    const entities = [];
    if (this.wither) {
      entities.push({
        position: this.wither.position,
        health: this.wither.health,
        maxHealth: this.wither.maxHealth,
        type: 'WITHER',
        mesh: this.wither.mesh,
        hitRadius: 3.0,
      });
    }
    if (this.dragon) {
      entities.push({
        position: this.dragon.position,
        health: this.dragon.health,
        maxHealth: this.dragon.maxHealth,
        type: 'DRAGON',
        mesh: this.dragon.mesh,
        hitRadius: 4.0,  // Larger hit radius for dragon
      });
      // Also include alive crystals as hittable entities
      for (let i = 0; i < this.endCrystals.length; i++) {
        const crystal = this.endCrystals[i];
        if (crystal.alive) {
          entities.push({
            position: crystal.position,
            health: 1,
            maxHealth: 1,
            type: 'END_CRYSTAL',
            mesh: crystal.mesh,
            hitRadius: 1.5,
            crystalIndex: i,
          });
        }
      }
    }
    return entities;
  }

  /** Clean up all resources */
  dispose() {
    // Dispose wither
    if (this.wither) {
      if (this.wither.mesh) {
        this.scene.remove(this.wither.mesh);
        this.wither.mesh.traverse(child => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(m => m.dispose());
            } else {
              child.material.dispose();
            }
          }
        });
      }
      this.wither = null;
    }

    for (let i = this.witherSkulls.length - 1; i >= 0; i--) {
      this._removeSkull(i);
    }

    // Dispose dragon
    if (this.dragon) {
      if (this.dragon.mesh) {
        this.scene.remove(this.dragon.mesh);
        this.dragon.mesh.traverse(child => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(m => m.dispose());
            } else {
              child.material.dispose();
            }
          }
        });
      }
      this.dragon = null;
    }

    // Dispose crystals
    for (const crystal of this.endCrystals) {
      if (crystal.mesh) {
        this.scene.remove(crystal.mesh);
        crystal.mesh.traverse(child => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(m => m.dispose());
            } else {
              child.material.dispose();
            }
          }
        });
      }
    }
    this.endCrystals = [];

    // Dispose particles
    for (const p of this.particles) {
      this.scene.remove(p.mesh);
      p.mesh.geometry.dispose();
      p.mesh.material.dispose();
    }
    this.particles = [];

    // Dispose health bars
    if (this.healthBar) {
      this.healthBar.dispose();
      this.healthBar = null;
    }
    if (this.dragonHealthBar) {
      this.dragonHealthBar.dispose();
      this.dragonHealthBar = null;
    }

    // Clear pending timers
    for (const t of this._pendingTimers) clearTimeout(t);
    this._pendingTimers = [];
  }
}
